/**
 * Interactive Network Graph Component
 * Allows users to drag and drop nodes to adjust positions
 * Positions can be saved and restored
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon } from '@mui/icons-material';
import type { NetworkGraphData, NetworkNode, NetworkEdge } from '../utils/networkGraphGenerator';

interface InteractiveNetworkGraphProps {
  graphData: NetworkGraphData;
  width: number;
  height: number;
  storageKey?: string; // Key for localStorage to save positions
  onPositionsChange?: (nodes: NetworkNode[]) => void;
}

/**
 * Calculate radar chart path for mini radar in network graph
 */
function calculateMiniRadarPath(
  scores: { A: number; B: number; C: number; D: number },
  centerX: number,
  centerY: number,
  maxRadius: number
): string {
  const maxVal = 12;
  
  const getPoint = (angle: number, value: number) => {
    const rad = (angle * Math.PI) / 180;
    const r = (value / maxVal) * maxRadius;
    return {
      x: centerX + r * Math.cos(rad),
      y: centerY - r * Math.sin(rad),
    };
  };
  
  const pA = getPoint(135, scores.A);
  const pB = getPoint(225, scores.B);
  const pC = getPoint(315, scores.C);
  const pD = getPoint(45, scores.D);
  
  return `M${pA.x} ${pA.y} L${pB.x} ${pB.y} L${pC.x} ${pC.y} L${pD.x} ${pD.y} Z`;
}

export const InteractiveNetworkGraph = ({
  graphData,
  width,
  height,
  storageKey,
  onPositionsChange,
}: InteractiveNetworkGraphProps) => {
  const [nodes, setNodes] = useState<NetworkNode[]>(graphData.nodes);
  const [edges] = useState<NetworkEdge[]>(graphData.edges);
  const [draggingNode, setDraggingNode] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  
  const nodeRadius = 12;
  const padding = 25;
  
  // Load saved positions from localStorage
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const savedPositions = JSON.parse(saved) as { [id: number]: { x: number; y: number } };
          setNodes(prev => prev.map(node => {
            const savedPos = savedPositions[node.id];
            if (savedPos) {
              return { ...node, x: savedPos.x, y: savedPos.y };
            }
            return node;
          }));
        } catch (e) {
          console.error('Failed to load saved positions:', e);
        }
      }
    }
  }, [storageKey]);
  
  // Update nodes when graphData changes
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const savedPositions = JSON.parse(saved) as { [id: number]: { x: number; y: number } };
          setNodes(graphData.nodes.map(node => {
            const savedPos = savedPositions[node.id];
            if (savedPos) {
              return { ...node, x: savedPos.x, y: savedPos.y };
            }
            return node;
          }));
          return;
        } catch (e) {
          // Fall through to default
        }
      }
    }
    setNodes(graphData.nodes);
  }, [graphData.nodes, storageKey]);
  
  // Get SVG coordinates from mouse event
  const getSvgCoordinates = useCallback((e: React.MouseEvent | MouseEvent) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const scaleX = width / rect.width;
    const scaleY = height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, [width, height]);
  
  // Handle mouse down on node
  const handleMouseDown = useCallback((e: React.MouseEvent, nodeId: number) => {
    e.preventDefault();
    const coords = getSvgCoordinates(e);
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      setDraggingNode(nodeId);
      setDragOffset({
        x: coords.x - node.x,
        y: coords.y - node.y,
      });
    }
  }, [nodes, getSvgCoordinates]);
  
  // Handle mouse move
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (draggingNode === null) return;
    
    const coords = getSvgCoordinates(e);
    const newX = Math.max(padding, Math.min(width - padding, coords.x - dragOffset.x));
    const newY = Math.max(padding, Math.min(height - padding - 10, coords.y - dragOffset.y));
    
    setNodes(prev => prev.map(node => {
      if (node.id === draggingNode) {
        return { ...node, x: newX, y: newY };
      }
      return node;
    }));
  }, [draggingNode, dragOffset, width, height, getSvgCoordinates]);
  
  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    if (draggingNode !== null) {
      setDraggingNode(null);
      onPositionsChange?.(nodes);
    }
  }, [draggingNode, nodes, onPositionsChange]);
  
  // Add/remove event listeners
  useEffect(() => {
    if (draggingNode !== null) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingNode, handleMouseMove, handleMouseUp]);
  
  // Save positions to localStorage
  const handleSave = useCallback(() => {
    if (storageKey) {
      const positions: { [id: number]: { x: number; y: number } } = {};
      nodes.forEach(node => {
        positions[node.id] = { x: node.x, y: node.y };
      });
      localStorage.setItem(storageKey, JSON.stringify(positions));
      alert('位置を保存しました');
    }
  }, [nodes, storageKey]);
  
  // Reset to original positions
  const handleReset = useCallback(() => {
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    setNodes(graphData.nodes);
    onPositionsChange?.(graphData.nodes);
  }, [graphData.nodes, storageKey, onPositionsChange]);
  
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!storageKey}
        >
          位置を保存
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
        >
          リセット
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 2 }}>
          ノードをドラッグして位置を調整できます
        </Typography>
      </Stack>
      
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{ 
          width: '100%',
          height: 'auto',
          maxWidth: width,
          border: '1px solid #e0e0e0', 
          borderRadius: 4,
          cursor: draggingNode !== null ? 'grabbing' : 'default',
        }}
      >
        {/* Background */}
        <rect width={width} height={height} fill="#fafafa" />
        
        {/* Edges */}
        <g className="edges">
          {edges.map((edge, idx) => {
            const source = nodes[edge.source];
            const target = nodes[edge.target];
            const strokeWidth = edge.tier === 1 ? 2.5 : 1;
            const opacity = edge.tier === 1 ? 0.7 : 0.4;
            
            return (
              <line
                key={idx}
                x1={source.x}
                y1={source.y}
                x2={target.x}
                y2={target.y}
                stroke="#6B7280"
                strokeWidth={strokeWidth}
                opacity={opacity}
              />
            );
          })}
        </g>
        
        {/* Nodes */}
        <g className="nodes">
          {nodes.map(node => {
            const radarPath = calculateMiniRadarPath(node.scores, node.x, node.y, nodeRadius);
            const isDragging = draggingNode === node.id;
            
            return (
              <g
                key={node.id}
                className="network-node"
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                onMouseDown={(e) => handleMouseDown(e, node.id)}
              >
                {/* Background circle */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={nodeRadius + 1}
                  fill="white"
                  stroke={isDragging ? '#3B82F6' : '#E5E7EB'}
                  strokeWidth={isDragging ? 2 : 0.5}
                />
                
                {/* Radar chart background */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={nodeRadius}
                  fill="#F9FAFB"
                  stroke="#E5E7EB"
                  strokeWidth={0.3}
                />
                
                {/* Radar chart path */}
                <path
                  d={radarPath}
                  fill="rgba(59, 130, 246, 0.3)"
                  stroke="#3B82F6"
                  strokeWidth={1}
                />
                
                {/* Name label */}
                <text
                  x={node.x}
                  y={node.y + nodeRadius + 10}
                  textAnchor="middle"
                  fontSize={8}
                  fill="#374151"
                  style={{ fontFamily: 'sans-serif', pointerEvents: 'none' }}
                >
                  {node.name}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </Box>
  );
};

export default InteractiveNetworkGraph;
